// lib/defaults/defaultSportsAndAmenities.ts
// Default categories and items for gyms
// Shared with mobile app defaults

export interface DefaultItem {
  name: string;
  category: string;
}

// ==================== SPORTS ====================
// Display order: Grappling, Striking, Mixed Martial Arts, Self Defense, Traditional Martial Arts, Functional Training, Mind & Body
export const SPORT_CATEGORIES = {
  GRAPPLING: 'Grappling',
  STRIKING: 'Striking',
  MIXED_MARTIAL_ARTS: 'Mixed Martial Arts',
  SELF_DEFENSE: 'Self Defense',
  TRADITIONAL_MARTIAL_ARTS: 'Traditional Martial Arts',
  FUNCTIONAL_TRAINING: 'Functional Training',
  MIND_BODY: 'Mind & Body',
} as const;

export const DEFAULT_SPORTS: DefaultItem[] = [
  // Grappling
  { name: 'Gi Jiu-Jitsu', category: 'Grappling' },
  { name: 'NoGi Jiu-Jitsu', category: 'Grappling' },
  { name: 'Wrestling', category: 'Grappling' },
  { name: 'Judo', category: 'Grappling' },

  // Striking
  { name: 'Boxing', category: 'Striking' },
  { name: 'Kick Boxing', category: 'Striking' },
  { name: 'Muay Thai', category: 'Striking' },

  // Mixed Martial Arts
  { name: 'MMA', category: 'Mixed Martial Arts' },
  { name: 'Sambo', category: 'Mixed Martial Arts' },
  { name: 'Combat Jui-Jitsu', category: 'Mixed Martial Arts' },

  // Self Defense
  { name: 'Krav Maga', category: 'Self Defense' },
  { name: 'Womens Self Defense', category: 'Self Defense' },

  // Traditional Martial Arts
  { name: 'Karate', category: 'Traditional Martial Arts' },
  { name: 'Tae Kwon Do', category: 'Traditional Martial Arts' },
  { name: 'Jeet Kun Do', category: 'Traditional Martial Arts' },
  { name: 'Aikido', category: 'Traditional Martial Arts' },

  // Functional Training
  { name: 'CrossFit', category: 'Functional Training' },
  { name: 'Strength Training', category: 'Functional Training' },
  { name: 'HIIT', category: 'Functional Training' },
  { name: 'Parkour', category: 'Functional Training' },
  { name: 'Calisthenics', category: 'Functional Training' },
  { name: 'Boot Camp', category: 'Functional Training' },
  { name: 'Circuit Training', category: 'Functional Training' },

  // Mind & Body
  { name: 'Yoga', category: 'Mind & Body' },
  { name: 'Breath Work', category: 'Mind & Body' },
  { name: 'Meditation', category: 'Mind & Body' },
];

// ==================== AMENITIES ====================
export const AMENITY_CATEGORIES = {
  CARDIO: 'Cardio Equipment',
  STRENGTH: 'Strength & Weights',
  GROUP_FITNESS: 'Group Fitness',
  RECOVERY: 'Recovery & Wellness',
  FACILITIES: 'Facilities',
  MEMBER_SERVICES: 'Member Services',
  NUTRITION: 'Nutrition & Hydration',
} as const;

export const DEFAULT_AMENITIES: DefaultItem[] = [
  // Cardio Equipment
  { name: 'Treadmills', category: 'Cardio Equipment' },
  { name: 'Stationary Bikes', category: 'Cardio Equipment' },
  { name: 'Recumbent Bikes', category: 'Cardio Equipment' },
  { name: 'Rowing Machines', category: 'Cardio Equipment' },
  { name: 'Elliptical Machines', category: 'Cardio Equipment' },
  { name: 'Stair Climbers', category: 'Cardio Equipment' },
  { name: 'Assault Bike', category: 'Cardio Equipment' },
  { name: 'Ski Machine', category: 'Cardio Equipment' },

  // Strength & Weights
  { name: 'Free Weights (Dumbbells)', category: 'Strength & Weights' },
  { name: 'Barbells', category: 'Strength & Weights' },
  { name: 'Weight Machines', category: 'Strength & Weights' },
  { name: 'Cable Machines', category: 'Strength & Weights' },
  { name: 'Smith Machine', category: 'Strength & Weights' },
  { name: 'Squat Racks', category: 'Strength & Weights' },
  { name: 'Power Racks', category: 'Strength & Weights' },
  { name: 'Bench Press', category: 'Strength & Weights' },
  { name: 'Plate Loaded Equipment', category: 'Strength & Weights' },
  { name: 'TRX/Suspension Training', category: 'Strength & Weights' },
  { name: 'Kettlebells', category: 'Strength & Weights' },
  { name: 'Medicine Balls', category: 'Strength & Weights' },
  { name: 'Battle Ropes', category: 'Strength & Weights' },

  // Group Fitness
  { name: 'Group Fitness Classes', category: 'Group Fitness' },
  { name: 'Yoga Studio', category: 'Group Fitness' },
  { name: 'Spin/Cycling Classes', category: 'Group Fitness' },
  { name: 'CrossFit Box', category: 'Group Fitness' },
  { name: 'Dance Classes', category: 'Group Fitness' },
  { name: 'HIIT Classes', category: 'Group Fitness' },
  { name: 'Pilates Studio', category: 'Group Fitness' },
  { name: 'Zumba Classes', category: 'Group Fitness' },
  { name: 'Boxing Classes', category: 'Group Fitness' },
  { name: 'Barre Classes', category: 'Group Fitness' },

  // Recovery & Wellness
  { name: 'Sauna', category: 'Recovery & Wellness' },
  { name: 'Steam Room', category: 'Recovery & Wellness' },
  { name: 'Hot Tub / Jacuzzi', category: 'Recovery & Wellness' },
  { name: 'Cold Plunge', category: 'Recovery & Wellness' },
  { name: 'Massage Services', category: 'Recovery & Wellness' },
  { name: 'Stretching Area', category: 'Recovery & Wellness' },
  { name: 'Recovery Pods', category: 'Recovery & Wellness' },
  { name: 'Infrared Sauna', category: 'Recovery & Wellness' },
  { name: 'Compression Therapy', category: 'Recovery & Wellness' },
  { name: 'Cryotherapy', category: 'Recovery & Wellness' },

  // Facilities
  { name: 'Locker Rooms', category: 'Facilities' },
  { name: 'Showers', category: 'Facilities' },
  { name: 'Towel Service', category: 'Facilities' },
  { name: 'Childcare', category: 'Facilities' },
  { name: 'Parking', category: 'Facilities' },
  { name: 'WiFi', category: 'Facilities' },
  { name: 'Basketball Court', category: 'Facilities' },
  { name: 'Swimming Pool', category: 'Facilities' },
  { name: 'Tennis Court', category: 'Facilities' },
  { name: 'Racquetball Court', category: 'Facilities' },
  { name: 'Squash Court', category: 'Facilities' },
  { name: 'Rock Climbing Wall', category: 'Facilities' },
  { name: 'Volleyball Court', category: 'Facilities' },
  { name: 'Battle Ropes Area', category: 'Facilities' },
  { name: 'Boxing Ring', category: 'Facilities' },
  { name: 'Turf / Sprint Track', category: 'Facilities' },
  { name: 'Outdoor Area', category: 'Facilities' },

  // Member Services
  { name: 'Personal Training', category: 'Member Services' },
  { name: 'Nutrition Coaching', category: 'Member Services' },
  { name: 'Physical Therapy', category: 'Member Services' },
  { name: 'Body Composition Analysis', category: 'Member Services' },
  { name: 'Phone App', category: 'Member Services' },
  { name: '24/7 Access', category: 'Member Services' },
  { name: 'Member Lounge', category: 'Member Services' },
  { name: 'Email Support', category: 'Member Services' },
  { name: 'Virtual Training', category: 'Member Services' },

  // Nutrition & Hydration
  { name: 'Cafe / Smoothie Bar', category: 'Nutrition & Hydration' },
  { name: 'Protein Bar', category: 'Nutrition & Hydration' },
  { name: 'Water Stations', category: 'Nutrition & Hydration' },
  { name: 'Supplement Shop', category: 'Nutrition & Hydration' },
];

// ==================== UTILITY FUNCTIONS ====================

/**
 * Returns sport categories in the correct display order
 */
export const getSportCategoriesInOrder = (): string[] => {
  return Object.values(SPORT_CATEGORIES);
};

export const getSportsByCategory = (category: string): DefaultItem[] => {
  return DEFAULT_SPORTS.filter(sport => sport.category === category);
};

export const getAmenitiesByCategory = (category: string): DefaultItem[] => {
  return DEFAULT_AMENITIES.filter(amenity => amenity.category === category);
};

/**
 * Get unique categories from default sports
 */
export const getDefaultSportCategories = (): string[] => {
  return Array.from(new Set(DEFAULT_SPORTS.map(s => s.category)));
};

/**
 * Get unique categories from default amenities
 */
export const getDefaultAmenityCategories = (): string[] => {
  return Array.from(new Set(DEFAULT_AMENITIES.map(a => a.category)));
};
